import { useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { AlertCircle, ArrowUp, CheckCircle, GitCompare, Loader2, Mail, Target, X } from "lucide-react";

export const CompetitorComparison = ({ t, language, userEmail, analysisUrl }) => {
  const [competitorUrls, setCompetitorUrls] = useState(['', '']);
  const [email, setEmail] = useState(userEmail || '');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [showEmailGate, setShowEmailGate] = useState(!userEmail);

  const addCompetitor = () => {
    if (competitorUrls.length < 4) setCompetitorUrls([...competitorUrls, '']);
  };

  const updateCompetitor = (i, val) => {
    const updated = [...competitorUrls];
    updated[i] = val;
    setCompetitorUrls(updated);
  };

  const handleCompare = async () => {
    if (!email || !email.includes('@')) { setShowEmailGate(true); return; }
    const validUrls = competitorUrls.filter(u => u.trim());
    if (validUrls.length === 0) return;
    setIsLoading(true);
    setResults(null);
    try {
      const res = await axios.post(`${API}/competitor-compare`, {
        my_url: analysisUrl || 'https://example.com',
        competitor_urls: validUrls.map(u => u.startsWith('http') ? u : `https://${u}`),
        email, language
      });
      setResults(res.data);
      setShowEmailGate(false);
    } catch (e) { console.error('Compare error:', e); }
    finally { setIsLoading(false); }
  };

  const scoreAreas = ['seo', 'ai_visibility', 'speed', 'content', 'backlinks', 'mobile'];
  const areaLabels = { seo: 'SEO', ai_visibility: 'AI Visibility', speed: 'Speed', content: 'Content', backlinks: 'Backlinks', mobile: 'Mobile' };
  const priorityColors = { high: 'bg-red-100 text-red-700', medium: 'bg-[#a855f7]/15 text-[#c084fc]', low: 'bg-[#a855f7]/10 text-[#a855f7]' };

  return (
    <section className="py-16 px-4 bg-[var(--color-bg)]" id="competitor-compare" data-testid="competitor-comparison-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#a855f7]/15 text-[#c084fc] text-sm font-semibold mb-4">
            <GitCompare className="w-4 h-4" />
            PRO
          </div>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight font-['Chivo'] mb-3">{t.competitor_title}</h2>
          <p className="text-[var(--color-text-muted)]">{t.competitor_subtitle}</p>
        </div>

        {/* Email Gate */}
        {showEmailGate && (
          <div className="max-w-md mx-auto mb-8 p-6 bg-[var(--color-surface)] border-2 border-[#a855f7]/30 rounded-xl text-center" data-testid="competitor-email-gate">
            <Mail className="w-10 h-10 text-[#a855f7] mx-auto mb-3" />
            <p className="text-sm font-medium text-[var(--color-text)] mb-4">{t.competitor_email_required}</p>
            <input type="email" data-testid="competitor-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com" className="w-full px-4 py-3 bg-[var(--color-bg)] text-[var(--color-text)] placeholder-[var(--color-text-muted)] border-2 border-[var(--color-border)] focus:border-[#a855f7] focus:outline-none mb-3" />
            <button onClick={() => { if(email.includes('@')) setShowEmailGate(false); }} data-testid="competitor-email-submit"
              className="w-full px-4 py-3 bg-[#a855f7] text-white font-semibold hover:bg-purple-600 transition-colors disabled:opacity-50"
              disabled={!email.includes('@')}>
              {t.competitor_compare}
            </button>
          </div>
        )}

        {!showEmailGate && (
          <>
            {/* Competitor URL Inputs */}
            <div className="max-w-2xl mx-auto mb-8 space-y-3">
              {competitorUrls.map((url, i) => (
                <div key={i} className="flex gap-2">
                  <span className="flex items-center justify-center w-8 h-12 bg-gray-200 text-xs font-bold text-[var(--color-text-muted)]">{i + 1}</span>
                  <input type="text" data-testid={`competitor-url-${i}`} value={url} onChange={(e) => updateCompetitor(i, e.target.value)}
                    placeholder={`competitor${i + 1}.com`} className="flex-1 px-4 py-3 bg-white text-gray-900 placeholder-gray-400 border-2 border-[var(--color-border)] focus:border-[#a855f7] focus:outline-none" />
                </div>
              ))}
              <div className="flex gap-3 justify-center">
                {competitorUrls.length < 4 && (
                  <button onClick={addCompetitor} className="text-sm text-[#a855f7] font-semibold hover:underline" data-testid="add-competitor-btn">+ {t.competitor_add}</button>
                )}
                <button data-testid="compare-btn" onClick={handleCompare} disabled={isLoading || !competitorUrls.some(u => u.trim())}
                  className="px-6 py-3 bg-[#a855f7] text-white font-semibold hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-2">
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <GitCompare className="w-5 h-5" />}
                  {t.competitor_compare}
                </button>
              </div>
            </div>

            {/* Results */}
            {results && (
              <div className="space-y-8 animate-fade-in" data-testid="comparison-results">
                {/* Score Comparison Table */}
                <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[var(--color-bg)] border-b border-[var(--color-border)]">
                        <tr>
                          <th className="text-left px-4 py-3 font-semibold text-[var(--color-text-muted)]">Metric</th>
                          <th className="text-center px-4 py-3 font-semibold text-[#a855f7]">{t.competitor_your_site}</th>
                          {results.competitors?.map((c, i) => (
                            <th key={i} className="text-center px-4 py-3 font-semibold text-[var(--color-text-muted)]">{c.name || c.url}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {scoreAreas.map(area => (
                          <tr key={area} className="border-b border-[var(--color-border)]">
                            <td className="px-4 py-3 font-medium">{areaLabels[area]}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`font-mono font-bold ${results.my_scores?.[area] >= 70 ? 'text-green-600' : results.my_scores?.[area] >= 50 ? 'text-[#a855f7]' : 'text-red-600'}`}>{results.my_scores?.[area] || 0}</span>
                            </td>
                            {results.competitors?.map((c, i) => {
                              const myScore = results.my_scores?.[area] || 0;
                              const theirScore = c.scores?.[area] || 0;
                              return (
                                <td key={i} className="px-4 py-3 text-center">
                                  <span className={`font-mono font-bold ${theirScore > myScore ? 'text-red-600' : 'text-green-600'}`}>{theirScore}</span>
                                  {theirScore > myScore && <ArrowUp className="w-3 h-3 text-red-400 inline ml-1" />}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Wins vs Losses */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-[var(--color-surface)] border border-green-200 rounded-xl p-6" data-testid="wins-list">
                    <h4 className="font-bold text-green-700 flex items-center gap-2 mb-4"><CheckCircle className="w-5 h-5" />{t.competitor_wins}</h4>
                    <div className="space-y-2">
                      {(results.where_you_win || []).map((w, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" /><span>{w}</span></div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-[var(--color-surface)] border border-red-200 rounded-xl p-6" data-testid="losses-list">
                    <h4 className="font-bold text-red-700 flex items-center gap-2 mb-4"><AlertCircle className="w-5 h-5" />{t.competitor_loses}</h4>
                    <div className="space-y-2">
                      {(results.where_you_lose || []).map((l, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm"><X className="w-4 h-4 text-red-500 flex-shrink-0" /><span>{l}</span></div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Action Plan */}
                <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden" data-testid="action-plan">
                  <div className="p-4 border-b bg-[var(--color-bg)]"><h4 className="font-bold flex items-center gap-2 font-['Chivo']"><Target className="w-5 h-5 text-[#a855f7]" />{t.competitor_action}</h4></div>
                  <div className="divide-y divide-gray-100">
                    {(results.action_plan || []).map((action, i) => (
                      <div key={i} className="p-4 flex items-start gap-4" data-testid={`action-${i}`}>
                        <span className={`px-2 py-1 text-xs font-bold rounded whitespace-nowrap ${priorityColors[action.priority] || 'bg-[var(--color-surface-alt)]'}`}>{action.priority}</span>
                        <div className="flex-1">
                          <p className="font-semibold text-sm">{action.area}</p>
                          <p className="text-sm text-[var(--color-text-muted)] mt-1">{action.action}</p>
                        </div>
                        <span className="text-xs font-medium text-emerald-400 bg-emerald-900/30 px-2 py-1 rounded whitespace-nowrap">{action.impact}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

// AI Blog Generator Component
